import {Anchor, Badge, Card, Divider, Group, Text} from "@mantine/core";
import {
  CardProps,
  DefaultText,
  EntityCommentList,
  FormattedDatabaseCode,
  FormattedDateTime,
  getTransportCategoryColorRefByType
} from "@ots-frontend/ots-ui";
import {Link} from "react-router-dom";
import {DispatchRequestEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";

export type DispatchRequestCardProps = Omit<CardProps, 'children'> & {
  dispatchRequest: DispatchRequestEntity
}

export const DispatchRequestCard = (props: DispatchRequestCardProps) => {
  const {dispatchRequest, ...rest} = props

  const {t} = useTranslation(["translation", "common"])

  return (
    <Card {...rest}>
      <Group position="apart" mt="md" mb="xs">
        <Text weight={500}>Dispatch Request Details</Text>
        {dispatchRequest.serviceTypeId ? (
          <Badge color="orange" variant="light">
            {t("common.entity.transport.worktype.service", "Service Work")}
          </Badge>
        ) : (
          <Badge color={getTransportCategoryColorRefByType(dispatchRequest.category)} variant="light">
            <Text color="white">
              <FormattedDatabaseCode value={dispatchRequest.category}/>
            </Text>
          </Badge>
        )
        }
      </Group>

      {(dispatchRequest.operatorName || dispatchRequest.externalSystem) &&
        <Group mt="md" mb="xs">
          {dispatchRequest.operatorName &&
            <Text size="md">
              {dispatchRequest.operatorName}
            </Text>
          }
          {dispatchRequest.externalSystem &&
            <Badge color="warning">
              {t("common.entity.dispatchrequest.profile.externalrequest", "External Request") + ": " + dispatchRequest.externalSystem}
            </Badge>
          }
        </Group>
      }

      <Divider my="sm" orientation="horizontal" variant="dashed"/>

      <Text size="lg" mt="md" weight={500}>
        {`${dispatchRequest.quantityDisplay} of ${dispatchRequest.freightTypeName}`}
      </Text>

      {dispatchRequest.serviceTypeId &&
        <Text size="md" mb="xs" color="orange">
          <DefaultText value={dispatchRequest.serviceTypeName}/>
        </Text>
      }

      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.dispatchrequest.info.field.scheduleddate.label", "Scheduled Date")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        <FormattedDateTime date={dispatchRequest.scheduledDttm}/>
      </Text>

      <Text ta="left" fz="sm" weight={500} mt="md">
        {t("profile.dispatchrequest.info.field.scheduledenddate.label", "Completion Deadline")}
      </Text>
      <Text ta="left" c="dimmed" fz="xs">
        <FormattedDateTime date={dispatchRequest.scheduledEndDttm}/>
      </Text>

      {dispatchRequest.pickupSiteId &&
        <>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("profile.dispatchrequest.info.field.pickupsite.label", "Pickup Site")}
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            {dispatchRequest.pickupSiteId ? (
              <Anchor
                component={Link}
                to={`/sites/profile?id=${dispatchRequest.pickupSiteId}`}>
                <DefaultText value={dispatchRequest.pickupSiteName}/>
              </Anchor>
            ) : (
              t("common.shared.unspecified", "Unspecified")
            )}
          </Text>
        </>
      }

      {dispatchRequest.deliverySiteId &&
        <>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("profile.dispatchrequest.info.field.deliverysite.label", "Delivery Site")}
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            {dispatchRequest.deliverySiteId ? (
              <Anchor
                component={Link}
                to={`/sites/profile?id=${dispatchRequest.deliverySiteId}`}>
                <DefaultText value={dispatchRequest.deliverySiteName}/>
              </Anchor>
            ) : (
              t("common.shared.unspecified", "Unspecified")
            )}
          </Text>
        </>
      }

      <Divider my="sm" orientation="horizontal" variant="dashed"/>

      <Group position="left" mt="md" mb="xs">
        <EntityCommentList objectId={dispatchRequest.id} showEmpty={false}/>
      </Group>
    </Card>
  )
}
